import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Code, FileText, ChevronDown, ChevronRight, Plus, Trash2, Edit2, 
  Download, Loader2, ToggleLeft, Layers, Zap, BookOpen, ShieldAlert, Lock
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';
import BotSkillEditorDialog from './BotSkillEditorDialog';

/**
 * Strategy badge colors and labels
 */
const STRATEGY_CONFIG = {
  always: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Always', dot: 'bg-emerald-500' },
  referenced: { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Referenced', dot: 'bg-amber-500' },
  manual: { color: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Manual', dot: 'bg-slate-400' }
};

/**
 * ⚠️ SECURITY NOTE — READ BEFORE MODIFYING
 * 
 * Bot Skills are injected directly into bot system prompts at runtime.
 * A malicious or poorly written skill can alter bot behavior, cause data leaks,
 * or produce harmful outputs. This is equivalent to modifying bot instructions.
 * 
 * Access to create/edit/delete skills is gated by the `bot_skills_manage` RBAC 
 * permission, which defaults to Commander in Chief only. This is intentional.
 * 
 * If importing skills from external sources (SkillsMP, GitHub, etc.):
 * - NEVER import scripts or executable code — only markdown instruction content
 * - Review ALL content before saving — check for hidden instructions, HTML comments,
 *   suspicious URLs, references to credentials/env vars, base64-encoded blocks
 * - Test skills on non-critical bots first
 * - Skills from community marketplaces are NOT audited — treat them like untrusted code
 * 
 * See: https://snyk.io/blog/toxicskills-malicious-ai-agent-skills-clawhub/
 *      https://arxiv.org/html/2602.06547v1
 */

export default function BotSkillsSection({ profile, isOwner, canManageSkills = false }) {
  const queryClient = useQueryClient();
  const [skillsSectionOpen, setSkillsSectionOpen] = React.useState(false);
  const [expandedSkills, setExpandedSkills] = React.useState({});
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingSkill, setEditingSkill] = React.useState(null);

  // Fetch skills for this bot
  const { data: skills = [], isLoading } = useQuery({
    queryKey: ['botSkills', profile?.id],
    queryFn: async () => {
      const allSkills = await base44.entities.BotSkill.filter({
        bot_profile_id: profile.id
      });
      // Sort by priority (lower = higher priority)
      return (allSkills || []).sort((a, b) => (a.priority || 5) - (b.priority || 5));
    },
    enabled: !!profile?.id
  });

  // Create skill mutation
  const createSkillMutation = useMutation({
    mutationFn: async (skillData) => {
      return base44.entities.BotSkill.create({
        ...skillData,
        bot_profile_id: profile.id,
        created_by: profile.bot_owner_email || ''
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botSkills', profile?.id] });
      setEditorOpen(false);
      setEditingSkill(null);
    }
  });

  // Update skill mutation
  const updateSkillMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return base44.entities.BotSkill.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botSkills', profile?.id] });
      setEditorOpen(false);
      setEditingSkill(null);
    }
  });

  // Delete skill mutation
  const deleteSkillMutation = useMutation({
    mutationFn: async (id) => {
      return base44.entities.BotSkill.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botSkills', profile?.id] });
    }
  });

  // Toggle active state
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }) => {
      return base44.entities.BotSkill.update(id, { is_active });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['botSkills', profile?.id] });
    }
  });

  const handleSave = (skillData) => {
    if (editingSkill) {
      updateSkillMutation.mutate({ id: editingSkill.id, data: skillData });
    } else {
      createSkillMutation.mutate(skillData);
    }
  };

  const handleEdit = (skill) => {
    setEditingSkill(skill);
    setEditorOpen(true);
  };

  const handleNew = () => {
    setEditingSkill(null);
    setEditorOpen(true);
  };

  const handleDelete = (skill) => {
    if (window.confirm(`Delete skill "${skill.name}"? This cannot be undone.`)) {
      deleteSkillMutation.mutate(skill.id);
    }
  };

  const toggleSkillExpand = (skillId) => {
    setExpandedSkills(prev => ({ ...prev, [skillId]: !prev[skillId] }));
  };

  // Stats
  const activeSkills = skills.filter(s => s.is_active);
  const totalTokens = skills.reduce((sum, s) => sum + (s.token_estimate || 0), 0);
  const activeTokens = activeSkills.reduce((sum, s) => sum + (s.token_estimate || 0), 0);

  return (
    <>
      <Collapsible open={skillsSectionOpen} onOpenChange={setSkillsSectionOpen}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
            <div className="flex items-center gap-2">
              {skillsSectionOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
              <Code className="w-4 h-4 text-violet-500" />
              <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">SKILLS.MD</h4>
              <Badge variant="outline" className="text-xs border-violet-500/30 text-violet-500">
                {activeSkills.length} active / {skills.length} total
              </Badge>
              {activeTokens > 0 && (
                <Badge variant="outline" className="text-xs border-slate-300 text-slate-400 font-mono">
                  ~{activeTokens.toLocaleString()} tokens
                </Badge>
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-2 space-y-2">
            {/* Security warning banner */}
            {canManageSkills && (
              <Alert className="bg-amber-50 border-amber-200">
                <ShieldAlert className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-xs text-amber-700">
                  <strong>Security notice:</strong> Skills are injected into bot context and can alter behavior. 
                  Only import content you've reviewed. Never paste scripts from untrusted sources. 
                  Community marketplace skills are NOT audited.
                </AlertDescription>
              </Alert>
            )}

            {/* Info bar */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Domain knowledge files that enhance this bot's capabilities. Skills are injected into context at runtime.
              </p>
              {canManageSkills ? (
                <Button
                  size="sm"
                  onClick={handleNew}
                  className="h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Skill
                </Button>
              ) : isOwner && !canManageSkills ? (
                <Badge variant="outline" className="text-xs border-amber-300 text-amber-600">
                  <Lock className="w-3 h-3 mr-1" />
                  View Only — manage requires bot_skills_manage permission
                </Badge>
              ) : null}
            </div>

            {/* Skills list */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : skills.length > 0 ? (
              <div className="space-y-2">
                {skills.map((skill) => {
                  const strategy = STRATEGY_CONFIG[skill.loading_strategy] || STRATEGY_CONFIG.always;
                  const isExpanded = expandedSkills[skill.id];
                  const sections = skill.section_index || [];

                  return (
                    <Collapsible
                      key={skill.id}
                      open={isExpanded}
                      onOpenChange={() => toggleSkillExpand(skill.id)}
                    >
                      <div className={`bg-white rounded-lg border transition-colors ${
                        skill.is_active ? 'border-slate-200' : 'border-slate-200 opacity-60'
                      }`}>
                        {/* Skill header row */}
                        <div className="flex items-center gap-2 p-3">
                          <CollapsibleTrigger className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            )}
                            <FileText className="w-4 h-4 text-violet-400 flex-shrink-0" />
                            <div className="min-w-0 text-left">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-slate-700 truncate">
                                  {skill.name}
                                </span>
                                <span className="text-xs font-mono text-slate-400 truncate hidden sm:inline">
                                  {skill.file_name}
                                </span>
                              </div>
                              {skill.description && (
                                <p className="text-xs text-slate-400 truncate mt-0.5">
                                  {skill.description}
                                </p>
                              )}
                            </div>
                          </CollapsibleTrigger>

                          {/* Right side controls */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Strategy badge */}
                            <Badge variant="outline" className={`text-xs ${strategy.color} hidden sm:flex`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${strategy.dot} mr-1`} />
                              {strategy.label}
                            </Badge>

                            {/* Priority */}
                            <Badge variant="outline" className="text-xs border-slate-200 text-slate-400 font-mono hidden sm:flex">
                              P{skill.priority || 5}
                            </Badge>

                            {/* Token count */}
                            <Badge variant="outline" className="text-xs border-slate-200 text-slate-400 font-mono hidden md:flex">
                              ~{(skill.token_estimate || 0).toLocaleString()}t
                            </Badge>

                            {/* Sections count */}
                            {sections.length > 0 && (
                              <Badge variant="outline" className="text-xs border-violet-200 text-violet-500 hidden md:flex">
                                <Layers className="w-3 h-3 mr-1" />
                                {sections.length}
                              </Badge>
                            )}

                            {/* Active toggle */}
                            {canManageSkills && (
                              <Switch
                                checked={skill.is_active !== false}
                                onCheckedChange={(checked) => 
                                  toggleActiveMutation.mutate({ id: skill.id, is_active: checked })
                                }
                                className="data-[state=checked]:bg-violet-600"
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}

                            {/* Actions */}
                            {canManageSkills && (
                              <div className="flex gap-0.5">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); handleEdit(skill); }}
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-violet-500"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); handleDelete(skill); }}
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-rose-500"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Expanded content */}
                        <CollapsibleContent>
                          <div className="border-t border-slate-100 p-3 space-y-3">
                            {/* Section chips */}
                            {sections.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-slate-500 mb-1.5">Sections</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {sections.map((section, idx) => (
                                    <div key={idx} className="bg-slate-50 px-2 py-1 rounded border border-slate-200">
                                      <span className="text-xs font-medium text-slate-600">{section.header}</span>
                                      <span className="text-xs text-slate-400 ml-1.5">~{section.token_estimate}t</span>
                                      <div className="flex gap-1 mt-0.5">
                                        {section.tags?.slice(0, 4).map((tag, tidx) => (
                                          <span key={tidx} className="text-[10px] text-violet-500 bg-violet-50 px-1 rounded">
                                            {tag}
                                          </span>
                                        ))}
                                        {(section.tags?.length || 0) > 4 && (
                                          <span className="text-[10px] text-slate-400">+{section.tags.length - 4}</span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Content preview */}
                            <div>
                              <p className="text-xs font-medium text-slate-500 mb-1.5">Content Preview</p>
                              <div className="bg-slate-50 p-3 rounded border border-slate-200 prose prose-xs prose-slate max-w-none max-h-[200px] overflow-y-auto break-words">
                                <ReactMarkdown>
                                  {(() => {
                                    // Strip frontmatter for preview
                                    const c = skill.content || '';
                                    const match = c.match(/^---\s*\n[\s\S]*?\n---\s*\n([\s\S]*)$/);
                                    const body = match ? match[1] : c;
                                    return body.length > 2000 ? body.substring(0, 2000) + '\n\n*... truncated ...*' : body;
                                  })()}
                                </ReactMarkdown>
                              </div>
                            </div>

                            {/* Metadata footer */}
                            <div className="flex items-center justify-between text-xs text-slate-400">
                              <div className="flex items-center gap-3">
                                {skill.change_note && (
                                  <span>Last change: {skill.change_note}</span>
                                )}
                                {skill.created_by && (
                                  <span>By: {skill.created_by}</span>
                                )}
                              </div>
                              {skill.created_date && (
                                <span>Created {format(new Date(skill.created_date), 'MMM d, yyyy')}</span>
                              )}
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white p-6 rounded-lg border border-dashed border-slate-300 text-center">
                <BookOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No skills configured yet.</p>
                <p className="text-xs text-slate-400 mt-1">
                  Skills are markdown files that give this bot domain-specific expertise.
                </p>
                {canManageSkills && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleNew}
                    className="mt-3 border-violet-300 text-violet-600 hover:bg-violet-50"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Create First Skill
                  </Button>
                )}
                {isOwner && !canManageSkills && (
                  <p className="text-xs text-amber-500 mt-3">
                    <Lock className="w-3 h-3 inline mr-1" />
                    You need the <strong>bot_skills_manage</strong> permission to create skills. Contact your Commander in Chief.
                  </p>
                )}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Skill Editor Dialog */}
      <BotSkillEditorDialog
        open={editorOpen}
        onOpenChange={(open) => { setEditorOpen(open); if (!open) setEditingSkill(null); }}
        skill={editingSkill}
        onSave={handleSave}
        saving={createSkillMutation.isPending || updateSkillMutation.isPending}
      />
    </>
  );
}